import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { DoxMascot } from "@/components/Logo";
import api from "@/lib/api";

/**
 * Dox Preview\u2122 — a calm, Apple-level overlay that walks the customer
 * through what Dox would do to handle a real renewal. One question per
 * screen. Large type. Generous whitespace. Subtle motion. Never software.
 *
 * Screens:
 *   0. Intro     \u2014 "Watch me handle this."
 *   1..8. Steps  \u2014 one per Dox action, revealed one at a time
 *   9. Closing   \u2014 "This was Dox Preview. When you're ready, I can do it for real."
 */
export default function DoxPreview({ open, onClose }) {
  const [screen, setScreen] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  // Reset & (once) call the backend when the overlay opens.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setScreen(0);
      setPreview(null);
      setError(null);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    setLoading(true);
    api
      .post("/preview/simulate-renewal")
      .then((r) => setPreview(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 401) {
          setError("Sign in and I'll show you how I'd handle this.");
        } else {
          setError("I couldn't set up the preview just now. Try again in a moment.");
        }
      })
      .finally(() => setLoading(false));
  }, [open]);

  const steps = preview?.trace || [];
  const totalScreens = 2 + steps.length; // intro + N steps + closing
  const isIntro = screen === 0;
  const isClosing = screen === totalScreens - 1;
  const activeStep = isIntro || isClosing ? null : steps[screen - 1];

  const advance = useCallback(() => {
    if (loading) return;
    setScreen((s) => Math.min(s + 1, totalScreens - 1));
  }, [loading, totalScreens]);

  // Keyboard: Enter/Space advances, Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.key === "Enter" || e.key === " ") && !isClosing) {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isClosing, advance, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="dox-preview-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] bg-[#05070f]/95 backdrop-blur-xl overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Dox Preview"
        >
          {/* Ambient depth — one soft brand-blue glow, off-center */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, rgba(30,120,255,0.35), transparent 60%)" }}
          />

          {/* Close */}
          <button
            data-testid="dox-preview-close"
            onClick={onClose}
            aria-label="Close Dox Preview"
            className="absolute top-6 right-6 sm:top-8 sm:right-8 z-[102] h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition"
          >
            <X size={18} />
          </button>

          {/* Discreet progress dots (only during step screens) */}
          {!isIntro && !isClosing && (
            <div
              data-testid="dox-preview-progress"
              className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-1.5 z-[101]"
            >
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i < screen ? "w-6 bg-dox-orange" : "w-2 bg-white/15"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Content stage */}
          <div className="relative min-h-full flex items-center justify-center px-6 py-24 sm:py-32">
            <div className="w-full max-w-2xl mx-auto">
              <AnimatePresence mode="wait">
                {loading && (
                  <ScreenShell key="loading">
                    <DoxMascot size={110} emotion="thinking" />
                    <div className="mt-10 text-white/60 text-sm tracking-wide">Dox is preparing your preview…</div>
                  </ScreenShell>
                )}

                {!loading && error && (
                  <ScreenShell key="error">
                    <DoxMascot size={110} emotion="idle" />
                    <p className="mt-10 text-white/80 text-lg text-center max-w-md">{error}</p>
                    <PrimaryButton onClick={onClose} testid="dox-preview-error-close">Close</PrimaryButton>
                  </ScreenShell>
                )}

                {!loading && !error && preview && isIntro && (
                  <IntroScreen
                    key="intro"
                    caseInfo={preview.case}
                    tier={preview.tier}
                    provenanceLine={preview.provenance_line}
                    onBegin={advance}
                  />
                )}

                {!loading && !error && preview && activeStep && (
                  <StepScreen
                    key={`step-${screen}`}
                    step={activeStep}
                    index={screen}
                    total={steps.length}
                    isLast={screen === steps.length}
                    onNext={advance}
                    caseInfo={preview.case}
                  />
                )}

                {!loading && !error && preview && isClosing && (
                  <ClosingScreen
                    key="closing"
                    closing={preview.closing}
                    recordId={preview.record_id}
                    onClose={onClose}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------------------------------- */
/* Screens                                                                    */
/* -------------------------------------------------------------------------- */

function ScreenShell({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.45, ease: [0.33, 1, 0.68, 1] }}
      className="flex flex-col items-center text-center"
    >
      {children}
    </motion.div>
  );
}

function IntroScreen({ caseInfo, tier, provenanceLine, onBegin }) {
  const isReal = tier === "license_dated" || tier === "license_undated";
  const isStock = tier === "stock";
  return (
    <ScreenShell>
      <DoxMascot size={120} emotion="greeting" />
      <div className="mt-10 text-[11px] uppercase tracking-[0.28em] text-dox-orange font-bold">
        Dox Preview
      </div>
      <h1
        data-testid="dox-preview-intro-title"
        className="mt-4 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]"
      >
        {isReal ? "Watch me handle yours." : "Watch me handle this."}
      </h1>
      <p className="mt-8 text-white/70 text-lg sm:text-xl leading-relaxed max-w-xl">
        {provenanceLine ||
          <>
            {"I'll walk you through exactly how I'd renew your "}
            <span className="text-white font-medium">{caseInfo?.classification}</span>
            {" with "}
            <span className="text-white font-medium">{caseInfo?.authority}</span>.
            No payment. No filing. Just so you can see me work.
          </>}
      </p>
      {isStock && (
        <div className="mt-6 text-white/40 text-xs tracking-wide max-w-md" data-testid="dox-preview-stock-hint">
          {"Upload a license and I'll walk yours instead."}
        </div>
      )}
      <PrimaryButton onClick={onBegin} testid="dox-preview-begin">Begin</PrimaryButton>
    </ScreenShell>
  );
}

function StepScreen({ step, index, total, isLast, onNext, caseInfo }) {
  return (
    <ScreenShell>
      <StepIcon emotion={emotionFor(step.code)} />
      <div className="mt-10 flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-[0.24em] font-bold">
        <Check size={13} strokeWidth={3} /> Step {index} of {total}
      </div>
      <h2
        data-testid={`dox-preview-step-${step.code}`}
        className="mt-4 font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.08] max-w-xl"
      >
        {step.headline}
      </h2>
      {step.detail && (
        <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed max-w-lg">
          {step.detail}
        </p>
      )}
      {step.code === "fee_identified" && caseInfo?.government_fee_usd && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">Government fee</div>
          <div className="mt-1 font-display font-black text-3xl text-white">
            ${caseInfo.government_fee_usd}
          </div>
        </div>
      )}
      <PrimaryButton onClick={onNext} testid="dox-preview-next">
        {isLast ? "See the receipt" : "Continue"}
      </PrimaryButton>
    </ScreenShell>
  );
}

function ClosingScreen({ closing, recordId, onClose }) {
  const verifyUrl = recordId ? `/verify/${recordId}` : null;
  return (
    <ScreenShell>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
      >
        <DoxMascot size={130} emotion="done" />
      </motion.div>
      <div className="mt-10 text-[11px] uppercase tracking-[0.28em] text-emerald-400 font-bold">
        Dox Preview Complete
      </div>
      <h2
        data-testid="dox-preview-closing-title"
        className="mt-4 font-display font-black text-white text-5xl sm:text-6xl tracking-[-0.025em] leading-[1.02]"
      >
        Dox completed this.
      </h2>
      <p className="mt-8 text-white/70 text-lg sm:text-xl leading-relaxed max-w-xl">
        {closing?.body || "No payment was processed. No government filing was submitted. When you're ready, I can perform this process for real."}
      </p>

      <div className="mt-12 flex flex-col sm:flex-row items-center gap-3">
        {verifyUrl && (
          <a
            data-testid="dox-preview-see-receipt"
            href={verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-8 py-4 min-h-[52px] shadow-[0_10px_40px_rgba(30,120,255,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
          >
            See what Dox completed
          </a>
        )}
        {verifyUrl && (
          <button
            data-testid="dox-preview-share"
            onClick={async () => {
              try { await navigator.clipboard.writeText(window.location.origin + verifyUrl); } catch { /* noop */ }
            }}
            className="rounded-full border border-white/15 hover:bg-white/5 text-white/85 font-semibold text-base px-6 py-4 min-h-[52px] transition inline-flex items-center gap-2"
          >
            Copy link
          </button>
        )}
        <button
          data-testid="dox-preview-done"
          onClick={onClose}
          className="rounded-full border border-white/15 hover:bg-white/5 text-white/85 font-semibold text-base px-8 py-4 min-h-[52px] transition"
        >
          {"I'm ready"}
        </button>
      </div>

      {recordId && (
        <div className="mt-10 text-[11px] tracking-[0.22em] uppercase text-white/30">
          Record {recordId}
        </div>
      )}
    </ScreenShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Bits                                                                       */
/* -------------------------------------------------------------------------- */

function PrimaryButton({ onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="mt-14 rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-9 py-4 min-h-[52px] shadow-[0_10px_40px_rgba(30,120,255,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98]"
    >
      {children}
    </button>
  );
}

function StepIcon({ emotion }) {
  // Uses Dox himself at every step so the customer always feels the employee.
  return (
    <motion.div
      key={emotion}
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
    >
      <DoxMascot size={100} emotion={emotion} />
    </motion.div>
  );
}

function emotionFor(code) {
  switch (code) {
    case "detected":
      return "thinking";
    case "prepared":
    case "fee_identified":
      return "reading";
    case "approval_pending":
      return "greeting";
    case "submitted":
    case "confirmed":
      return "thinking";
    case "certified":
    case "archived":
      return "done";
    default:
      return "idle";
  }
}
